import { useState, useCallback } from "react";
import DownloadManager, {
  DownloadOptions,
  DownloadResult,
  DownloadError,
} from "../utils/downloadUtils";
import { saveAs } from "file-saver";

interface UseDownloadManagerOptions {
  onSuccess?: (filename: string) => void;
  onError?: (error: DownloadError) => void;
  onProgress?: (message: string) => void;
}

interface DownloadState {
  isDownloading: boolean;
  downloadMessage: string;
  progress: number;
}

/**
 * Custom hook for managing file downloads with progress tracking and error handling
 *
 * @param options - Configuration options for the download manager
 * @param options.onSuccess - Optional callback invoked when downloads complete successfully
 * @param options.onError - Optional callback invoked when downloads fail
 * @param options.onProgress - Optional callback invoked to report download progress
 *
 * @returns An object containing:
 * - `isDownloading`: Boolean indicating if a download is in progress
 * - `downloadMessage`: String with current download status message
 * - `progress`: Number representing download progress (0-100)
 * - `downloadFile`: Function to download a single file
 * - `downloadMultipleFiles`: Function to download multiple files (individually or as ZIP)
 * - `reset`: Function to reset the download state
 *
 * @example
 * ```tsx
 * const {
 *   isDownloading,
 *   downloadMessage,
 *   progress,
 *   downloadFile,
 *   downloadMultipleFiles,
 *   reset
 * } = useDownloadManager({
 *   onSuccess: (filename) => console.log(`Downloaded: ${filename}`),
 *   onError: (error) => console.error('Download failed:', error),
 *   onProgress: (message) => console.log(message)
 * });
 *
 * // Download a single file
 * await downloadFile({ url: 'https://example.com/file.pdf', preferredName: 'document.pdf' });
 *
 * // Download multiple files as individual downloads
 * await downloadMultipleFiles([
 *   { url: 'https://example.com/file1.jpg', filename: 'image1.jpg' },
 *   { url: 'https://example.com/file2.jpg', filename: 'image2.jpg' }
 * ]);
 *
 * // Download multiple files as a ZIP archive
 * await downloadMultipleFiles([
 *   { url: 'https://example.com/file1.jpg', filename: 'image1.jpg' },
 *   { url: 'https://example.com/file2.jpg', filename: 'image2.jpg' }
 * ], 'images.zip');
 * ```
 */
export const useDownloadManager = (options: UseDownloadManagerOptions = {}) => {
  const [state, setState] = useState<DownloadState>({
    isDownloading: false,
    downloadMessage: "",
    progress: 0,
  });

  const updateState = useCallback((updates: Partial<DownloadState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Downloads a single file with progress tracking and error handling
   *
   * @param downloadOptions - Configuration for the file download
   * @param downloadOptions.url - The URL of the file to download
   * @param downloadOptions.preferredName - Optional preferred filename for the download
   * @returns Promise that resolves to a DownloadResult indicating success or failure
   *
   * @example
   * ```tsx
   * const result = await downloadFile({
   *   url: 'https://example.com/document.pdf',
   *   preferredName: 'my-document.pdf'
   * });
   *
   * if (result.success) {
   *   console.log('Download completed successfully');
   * } else {
   *   console.error('Download failed:', result.error);
   * }
   * ```
   */
  const downloadFile = useCallback(
    async (downloadOptions: DownloadOptions): Promise<DownloadResult> => {
      updateState({
        isDownloading: true,
        downloadMessage: "Starting download...",
      });

      try {
        const result = await DownloadManager.downloadFile(downloadOptions);

        if (result.success) {
          options.onSuccess?.(result.filename || "file");
          updateState({ downloadMessage: "Download completed successfully" });
        } else if (result.error) {
          options.onError?.(result.error);
          updateState({ downloadMessage: "Download failed" });
        }

        return result;
      } catch (error) {
        const downloadError: DownloadError = {
          type: "UNKNOWN",
          message: error instanceof Error ? error.message : "Unknown error",
          url: downloadOptions.url,
        };

        options.onError?.(downloadError);
        updateState({ downloadMessage: "Download failed" });

        return { success: false, error: downloadError };
      } finally {
        setTimeout(() => {
          updateState({
            isDownloading: false,
            downloadMessage: "",
            progress: 0,
          });
        }, 1000);
      }
    },
    [options, updateState]
  );

  /**
   * Downloads multiple files either individually or packaged in a ZIP archive
   *
   * @param urls - Array of objects containing URL and filename for each file to download
   * @param urls[].url - The URL of the file to download
   * @param urls[].filename - The desired filename for the downloaded file
   * @param zipFilename - Optional filename for creating a ZIP archive. If provided, all files will be packaged into this ZIP file. If omitted, files will be downloaded individually.
   * @returns Promise that resolves when all downloads are complete
   *
   * @example
   * ```tsx
   * // Download files individually
   * await downloadMultipleFiles([
   *   { url: 'https://example.com/file1.jpg', filename: 'photo1.jpg' },
   *   { url: 'https://example.com/file2.jpg', filename: 'photo2.jpg' },
   *   { url: 'https://example.com/file3.pdf', filename: 'document.pdf' }
   * ]);
   *
   * // Download files as a ZIP archive
   * await downloadMultipleFiles([
   *   { url: 'https://example.com/file1.jpg', filename: 'photo1.jpg' },
   *   { url: 'https://example.com/file2.jpg', filename: 'photo2.jpg' }
   * ], 'photos.zip');
   * ```
   */
  const downloadMultipleFiles = useCallback(
    async (
      urls: { url: string; filename: string }[],
      zipFilename?: string
    ): Promise<void> => {
      if (zipFilename) {
        // Create ZIP
        updateState({
          isDownloading: true,
          downloadMessage: "Creating ZIP archive...",
        });

        try {
          const result = await DownloadManager.createZipFromUrls(
            urls,
            zipFilename,
            (current, total, message) => {
              updateState({
                downloadMessage: message,
                progress: Math.round((current / total) * 100),
              });
              options.onProgress?.(message);
            }
          );

          if (result.success && result.blob) {
            saveAs(result.blob, zipFilename);
            options.onSuccess?.(zipFilename);
          } else {
            throw new Error(result.error || "ZIP creation failed");
          }
        } catch (error) {
          const downloadError: DownloadError = {
            type: "UNKNOWN",
            message:
              error instanceof Error ? error.message : "ZIP creation failed",
            url: "ZIP_CREATION",
          };
          options.onError?.(downloadError);
        }
      } else {
        // Download individually
        updateState({
          isDownloading: true,
          downloadMessage: "Starting downloads...",
        });

        const results = await DownloadManager.processWithConcurrency(
          urls,
          async (item, index) => {
            updateState({
              downloadMessage: `Downloading ${index + 1} of ${urls.length}...`,
              progress: Math.round(((index + 1) / urls.length) * 100),
            });
            options.onProgress?.(
              `Downloading ${index + 1} of ${urls.length}...`
            );

            await downloadFile({ url: item.url, preferredName: item.filename });
          },
          3
        );

        const successful = results.filter(
          (r) => r.status === "fulfilled"
        ).length;
        const failed = results.length - successful;

        if (failed > 0) {
          options.onError?.({
            type: "UNKNOWN",
            message: `${failed} of ${urls.length} downloads failed`,
            url: "BATCH_DOWNLOAD",
          });
        } else {
          options.onSuccess?.(`${successful} files downloaded`);
        }
      }

      setTimeout(() => {
        updateState({ isDownloading: false, downloadMessage: "", progress: 0 });
      }, 2000);
    },
    [downloadFile, options, updateState]
  );

  /**
   * Resets the download state to its initial values
   *
   * @example
   * ```tsx
   * // Reset download state after completion or error
   * reset();
   * ```
   */
  const reset = useCallback(() => {
    updateState({ isDownloading: false, downloadMessage: "", progress: 0 });
  }, [updateState]);

  return {
    ...state,
    downloadFile,
    downloadMultipleFiles,
    reset,
  };
};

export default useDownloadManager;
